import React, { useEffect, useState } from "react";
import { getAuditLogs } from "../api";
import Pagination from "./Pagination";
import "../styles/AuditLogs.css";

export default function AuditLogs({
  token,
  applicantId = null,
  onStatsChange,
}) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    action: "",
    adminUsername: "",
    startDate: "",
    endDate: "",
  });

  // 🔹 Local state just for the input, used for debouncing
  const [searchAdmin, setSearchAdmin] = useState("");

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);

  const loadLogs = async (pageNum = 1, limit = perPage) => {
    if (!token) return;
    setLoading(true);
    try {
      const params = {
        page: pageNum,
        limit,
        ...filters,
      };

      if (applicantId) {
        params.resourceId = applicantId;
      }

      const res = await getAuditLogs(token, params);

      if (res.data.logs) {
        setLogs(res.data.logs);
        setTotalItems(res.data.pagination.total);
        setPage(res.data.pagination.page);
        setPerPage(res.data.pagination.limit);

        // Update parent stats if callback provided
        if (onStatsChange) {
          onStatsChange({
            totalItems: res.data.pagination.total,
            page: res.data.pagination.page,
          });
        }
      }
    } catch (err) {
      alert("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 Debounce admin username filter
  useEffect(() => {
    const handler = setTimeout(() => {
      setFilters((prev) => ({
        ...prev,
        adminUsername: searchAdmin.trim(),
      }));
    }, 1000); // debounce delay (ms)

    return () => clearTimeout(handler);
  }, [searchAdmin]);

  useEffect(() => {
    setPage(1);
    loadLogs(1, perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, filters, applicantId]);

  useEffect(() => {
    if (!token) return;
    loadLogs(page, perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString();
  };

  const getActionBadgeClass = (action) => {
    switch (action) {
      case "UPDATE_SCORES":
        return "badge--warning";
      case "UPDATE_STATUS":
        return "badge--info";
      case "UPDATE_APPLICANT":
        return "badge--primary";
      case "EXPORT_DATA":
        return "badge--success";
      case "VIEW_APPLICANTS":
        return "badge--secondary";
      default:
        return "";
    }
  };

  const renderChanges = (changes) => {
    if (!changes || !changes.before || !changes.after) {
      return <span className="muted">-</span>;
    }

    const { before, after } = changes;
    const changedFields = [];

    // Check for score changes
    if (before.scores && after.scores) {
      Object.keys(after.scores).forEach((key) => {
        if (before.scores[key] !== after.scores[key]) {
          changedFields.push(
            `${key}: ${before.scores[key]} → ${after.scores[key]}`
          );
        }
      });
    }

    // Check for status changes
    if (before.finalStatus !== after.finalStatus) {
      changedFields.push(
        `Status: ${before.finalStatus} → ${after.finalStatus}`
      );
    }

    // Check for total score changes
    if (before.totalScore !== after.totalScore) {
      changedFields.push(`Total: ${before.totalScore} → ${after.totalScore}`);
    }

    return changedFields.length > 0 ? (
      <div className="changes-compact">
        {changedFields.map((change, idx) => (
          <div key={idx} className="change-line">
            {change}
          </div>
        ))}
      </div>
    ) : (
      <span className="muted">-</span>
    );
  };

  return (
    <>
      {/* Filters */}
      {!applicantId && (
        <div className="admin-controls">
          <div className="audit-filters-group">
            <div className="select-wrap">
              <label className="select-label">Action Type</label>
              <select
                className="select"
                value={filters.action}
                onChange={(e) =>
                  setFilters({ ...filters, action: e.target.value })
                }
              >
                <option value="">All Actions</option>
                <option value="UPDATE_SCORES">Update Scores</option>
                <option value="EXPORT_DATA">Export Data</option>
              </select>
            </div>

            <div className="select-wrap">
              <label className="select-label">Admin Username</label>
              <input
                type="text"
                className="select"
                placeholder="Filter by admin..."
                // 🔹 bind to debounced local state
                value={searchAdmin}
                onChange={(e) => setSearchAdmin(e.target.value)}
              />
            </div>

            <div className="select-wrap">
              <label className="select-label">Start Date</label>
              <input
                type="date"
                className="select"
                value={filters.startDate}
                onChange={(e) =>
                  setFilters({ ...filters, startDate: e.target.value })
                }
              />
            </div>

            <div className="select-wrap">
              <label className="select-label">End Date</label>
              <input
                type="date"
                className="select"
                value={filters.endDate}
                onChange={(e) =>
                  setFilters({ ...filters, endDate: e.target.value })
                }
              />
            </div>
          </div>

          <div className="btn-group">
            <button
              className="btn-outline"
              onClick={() => {
                setFilters({
                  action: "",
                  adminUsername: "",
                  startDate: "",
                  endDate: "",
                });
                // 🔹 also clear input when resetting filters
                setSearchAdmin("");
              }}
            >
              Reset Filters
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="table-card">
        {loading && (
          <div className="loading-overlay">Loading audit logs...</div>
        )}
        <div className="table-wrap">
          <table className="app-table">
            <thead>
              <tr>
                <th>Action</th>
                <th>Admin</th>
                <th>Timestamp</th>
                <th>Description</th>
                <th>Applicant</th>
                <th>Changes</th>
                {/* <th>IP Address</th> */}
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log._id}>
                  <td>
                    <span
                      className={`badge ${getActionBadgeClass(log.action)}`}
                    >
                      {log.action.replace(/_/g, " ")}
                    </span>
                  </td>
                  <td className="mono">{log.adminUsername}</td>
                  <td className="muted" style={{ fontSize: "0.82rem" }}>
                    {formatDate(log.createdAt)}
                  </td>
                  <td>{log.description}</td>
                  <td>
                    {log.applicantName ? (
                      <div>
                        <div className="mono">{log.applicantName}</div>
                        {log.applicantEmail && (
                          <div
                            className="muted"
                            style={{ fontSize: "0.78rem" }}
                          >
                            {log.applicantEmail}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="muted">-</span>
                    )}
                  </td>
                  <td>{renderChanges(log.changes)}</td>
                  {/* <td className="muted" style={{ fontSize: "0.8rem" }}>
                    {log.ipAddress || "-"}
                  </td> */}
                </tr>
              ))}
              {logs.length === 0 && !loading && (
                <tr>
                  <td colSpan={7} className="empty">
                    No audit logs found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination controls */}
      <div className="admin-footer">
        <Pagination
          totalItems={totalItems}
          currentPage={page}
          pageSize={perPage}
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newSize) => {
            setPerPage(newSize);
            setPage(1);
          }}
          pageSizeOptions={[10, 20, 50, 100]}
          siblingCount={1}
          boundaryCount={1}
          showJump={true}
        />

        <div className="admin-range">
          Showing{" "}
          <strong>
            {totalItems === 0
              ? 0
              : Math.min((page - 1) * perPage + 1, totalItems)}
          </strong>{" "}
          – <strong>{Math.min(page * perPage, totalItems)}</strong> of{" "}
          <strong>{totalItems}</strong>
        </div>
      </div>
    </>
  );
}
